// Text measurement for the PDF page planner, giving the SAME answer
// @react-pdf/renderer gives when it lays the text out: each script run is
// shaped by the real embedded font (fontkit, the same objects react-pdf's
// font store holds), and the line breaks come from a port of react-pdf's
// own breaker. Pure: the fonts are passed in (src/pdf/measure.ts resolves
// them from react-pdf inside the lazy pdf chunk), so this module and the
// planner stay free of react-pdf imports.
//
// Facts this mirrors, from @react-pdf/textkit 6.3.0 (lib/textkit.js):
// - text is itemised into runs wherever the Unicode script changes; each run
//   is shaped on its own (kerning never crosses a run boundary);
// - inside a run, words are split on U+0020 only (`.split(/([ ]+)/g)`); a
//   no-break space (formatCurrency prints "Rp 1.054.500" with one) never
//   breaks;
// - the hyphenation callback registered in src/pdf/fonts.ts splits a word
//   only at a soft hyphen (U+00AD) it already carries and otherwise returns
//   it whole, so a word is one box unless the planner has marked where it
//   may break (breakLongWords in pagePlan.ts does that for a word wider than
//   its column; nothing else is ever hyphenated). textkit drops the soft
//   hyphen characters themselves before measuring, and draws a hyphen glyph
//   when a line ends at such a break;
// - a word whose next syllable is not exactly one space (a run boundary
//   inside "10x", a double space) gets a hyphen break opportunity (a penalty
//   node of width 5, penalty 600);
// - a run of spaces is glue of width w, stretch w * 3 / 6, shrink w * 3 / 9;
// - the paragraph ends with glue of infinite stretch and a forced break;
// - line breaking is Knuth-Plass (demerits line 10, flagged 100, fitness
//   3000, tolerance 4, retried at +5 up to 50) with a best-fit fallback;
// - letterSpacing is added to every glyph's advance;
// - with no lineHeight style a line is (ascent - descent + lineGap) em tall.

export type Face = "Inter-400" | "Inter-500" | "Inter-600" | "InterTight-600";

// The slice of a fontkit font the measurer needs (react-pdf's font store
// hands out fontkit fonts; node checks can open the TTFs with fontkit).
export type ShapingFont = {
  unitsPerEm: number;
  ascent: number;
  descent: number;
  lineGap: number;
  // textkit always passes "ltr" so fontkit cannot reverse the string; the
  // port asks for the same.
  layout: (
    text: string,
    features?: unknown,
    script?: unknown,
    language?: unknown,
    direction?: "ltr" | "rtl",
  ) => {
    glyphs: { codePoints: number[] }[];
    positions: { xAdvance: number }[];
  };
  // react-pdf hands textkit the stack [Inter, Helvetica] and substitutes run
  // by run for any code point the first font has no glyph for, so a tab, an
  // emoji or a CJK character leaves the Inter run and measures 0pt in the
  // standard font. Modelled in shapingRuns.
  hasGlyphForCodePoint: (codePoint: number) => boolean;
};

export type TextMeasurer = {
  // Lines a single-style text wraps to inside `availableWidthPt`, exactly as
  // react-pdf breaks it. Apply textTransform (uppercase) before calling. One
  // paragraph only: a column whose value embeds its own "\n" would need
  // splitting here first, as react-pdf splits paragraphs on it.
  // Pass `subLine` for a cell that prints a second line under the value: the
  // sub run's newline ends the value's paragraph with a hyphen penalty, which
  // can wrap the last word a little earlier than its letters alone would.
  lines: (
    text: string,
    face: Face,
    size: number,
    letterSpacing: number,
    availableWidthPt: number,
    subLine?: boolean,
  ) => number;
  lineHeight: (face: Face, size: number) => number;
  // Width of a single-style text on one line (every advance plus the letter
  // spacing textkit adds to each glyph), soft hyphens dropped.
  width: (text: string, face: Face, size: number, letterSpacing: number) => number;
};

// ── Script runs ─────────────────────────────────────────────────────────────
// textkit's scriptItemizer: Common / Inherited / Unknown characters never
// start a run (they ride with the script around them; leading ones form a
// run of their own once the first real script appears).

const scriptOf = (cp: number): string => {
  if (
    (cp >= 0x41 && cp <= 0x5a) ||
    (cp >= 0x61 && cp <= 0x7a) ||
    cp === 0xaa ||
    cp === 0xba ||
    (cp >= 0xc0 && cp <= 0x24f && cp !== 0xd7 && cp !== 0xf7) ||
    (cp >= 0x250 && cp <= 0x2af) ||
    // Latin Extended Additional: Vietnamese (Ạ, ế, ộ) lives here.
    (cp >= 0x1e00 && cp <= 0x1eff) ||
    (cp >= 0x2c60 && cp <= 0x2c7f)
  )
    return "Latin";
  if (cp >= 0x300 && cp <= 0x36f) return "Inherited";
  if (cp >= 0x370 && cp <= 0x3ff) return "Greek";
  if (cp >= 0x400 && cp <= 0x4ff) return "Cyrillic";
  if (cp >= 0x590 && cp <= 0x5ff) return "Hebrew";
  if (cp >= 0x600 && cp <= 0x6ff) return "Arabic";
  if (cp >= 0xe00 && cp <= 0xe7f) return "Thai";
  if (cp >= 0x3040 && cp <= 0x309f) return "Hiragana";
  if (cp >= 0x30a0 && cp <= 0x30ff) return "Katakana";
  if (cp >= 0x4e00 && cp <= 0x9fff) return "Han";
  if (cp >= 0xac00 && cp <= 0xd7af) return "Hangul";
  return "Common";
};

const IGNORED_SCRIPTS = ["Common", "Inherited", "Unknown"];

// Run boundaries as [start, end) pairs over the whole string (textkit
// flattens the itemizer's runs with the full-length runs of its other
// engines, which yields exactly these segments).
export const scriptRuns = (chars: string[]): [number, number][] => {
  let lastScript = "Unknown";
  let lastIndex = 0;
  const bounds: number[] = [];
  chars.forEach((ch, i) => {
    const script = scriptOf(ch.codePointAt(0) ?? 0);
    if (script !== lastScript && !IGNORED_SCRIPTS.includes(script)) {
      if (lastScript !== "Unknown") bounds.push(lastIndex);
      lastIndex = i;
      lastScript = script;
    }
  });
  if (lastIndex < chars.length) bounds.push(lastIndex);
  const starts = Array.from(new Set([0, ...bounds])).sort((a, b) => a - b);
  return starts.map((start, i) => [start, i + 1 < starts.length ? starts[i + 1] : chars.length]);
};

export const SOFT_HYPHEN = "\u00AD";

export type ShapingRun = { start: number; end: number; shaped: boolean };

// The runs react-pdf actually shapes: a script run is cut again wherever the
// font stops having a glyph, because textkit substitutes the fallback font
// for that span and then tokenises per run. An unshaped span measures 0pt
// (the standard fallback font has no glyph either) and, being its own run,
// creates the token boundary react-pdf creates.
export const shapingRuns = (chars: string[], font: ShapingFont): ShapingRun[] => {
  const out: ShapingRun[] = [];
  scriptRuns(chars).forEach(([start, end]) => {
    let from = start;
    let shaped = font.hasGlyphForCodePoint(chars[start]?.codePointAt(0) ?? 0);
    for (let i = start + 1; i < end; i += 1) {
      const has = font.hasGlyphForCodePoint(chars[i].codePointAt(0) ?? 0);
      if (has !== shaped) {
        out.push({ start: from, end: i, shaped });
        from = i;
        shaped = has;
      }
    }
    if (from < end) out.push({ start: from, end, shaped });
  });
  return out;
};

// Advance of every character in points, each script run shaped on its own
// by the real font (kerning, contextual alternates and all), plus
// letterSpacing on every glyph (textkit's scalePositions never skips the
// last one). A ligature's advance lands on its first code point.
const advances = (
  chars: string[],
  font: ShapingFont,
  size: number,
  letterSpacing: number,
  runs: ShapingRun[],
): number[] => {
  const scale = size / font.unitsPerEm;
  const out: number[] = new Array(chars.length).fill(0);
  runs.forEach(({ start, end, shaped }) => {
    // A substituted span has no advance of its own. True for every character
    // the fallback cannot draw either, which is everything outside Helvetica's
    // WinAnsi set: the exceptions are the C1 controls U+0080-U+009F, which
    // only reach a report through mojibake.
    if (!shaped) return;
    const run = font.layout(chars.slice(start, end).join(""), undefined, undefined, undefined, "ltr");
    let i = start;
    run.glyphs.forEach((glyph, g) => {
      // The same code-point accounting react-pdf uses to map glyphs back to
      // string positions (textkit's `resolve`), so a ligature's advance
      // lands on its first code point here as it does there.
      const span = Math.max(1, glyph.codePoints.length);
      if (i < end) out[i] = run.positions[g].xAdvance * scale;
      i += span;
    });
  });
  return out.map((a) => a + letterSpacing);
};

// ── Knuth-Plass line breaking, ported from @react-pdf/textkit ───────────────
// (which carries Bram Stein's typeset implementation, new BSD License,
// Copyright 2009-2010 Bram Stein). Only the parts that decide WHERE the
// breaks fall are kept; the planner needs the line count, not the lines.

const INFINITY = 10000;
const GLUE_OPTS = { width: 3, stretch: 6, shrink: 9 };
const TOLERANCE_STEPS = 5;
const TOLERANCE_LIMIT = 50;

type Glue = { type: "glue"; width: number; stretch: number; shrink: number };
type Box = { type: "box"; width: number };
type Penalty = { type: "penalty"; width: number; penalty: number; flagged: number };
type Node = Glue | Box | Penalty;

type Totals = { width: number; stretch: number; shrink: number };
type Breakpoint = {
  position: number;
  demerits: number;
  line: number;
  fitnessClass: number;
  totals: Totals;
  previous: Breakpoint | null;
};

class ListNode {
  data: Breakpoint;
  prev: ListNode | null = null;
  next: ListNode | null = null;
  constructor(data: Breakpoint) {
    this.data = data;
  }
}

// textkit's list, pointer for pointer: a removed node KEEPS its prev/next,
// so the breaker's loop can walk on from a node it has just deactivated.
class LinkedList {
  head: ListNode | null = null;
  tail: ListNode | null = null;
  length = 0;
  isLinked(node: ListNode | null) {
    return !(
      (node && node.prev === null && node.next === null && this.tail !== node && this.head !== node) ||
      this.length === 0
    );
  }
  size() {
    return this.length;
  }
  first() {
    return this.head;
  }
  forEach(fn: (node: ListNode) => void) {
    let node = this.head;
    while (node !== null) {
      fn(node);
      node = node.next;
    }
  }
  push(node: ListNode) {
    if (this.head === null) {
      this.head = node;
      this.tail = node;
      node.prev = null;
      node.next = null;
    } else {
      node.prev = this.tail;
      node.next = null;
      if (this.tail) this.tail.next = node;
      this.tail = node;
    }
    this.length += 1;
  }
  insertBefore(node: ListNode, newNode: ListNode) {
    if (!this.isLinked(node)) return;
    newNode.prev = node.prev;
    newNode.next = node;
    if (node.prev === null) this.head = newNode;
    else node.prev.next = newNode;
    node.prev = newNode;
    this.length += 1;
  }
  remove(node: ListNode) {
    if (!this.isLinked(node)) return;
    if (node.prev === null) this.head = node.next;
    else node.prev.next = node.next;
    if (node.next === null) this.tail = node.prev;
    else node.next.prev = node.prev;
    this.length -= 1;
  }
}

const breakpoint = (
  position: number,
  demerits: number,
  line: number,
  fitnessClass: number,
  totals: Totals | undefined,
  previous: Breakpoint | null,
): Breakpoint => ({
  position,
  demerits,
  line,
  fitnessClass,
  totals: totals || { width: 0, stretch: 0, shrink: 0 },
  previous,
});

const computeCost = (
  nodes: Node[],
  lineLengths: number[],
  sum: Totals,
  end: number,
  active: Breakpoint,
  currentLine: number,
): number => {
  let width = sum.width - active.totals.width;
  const lineLength =
    currentLine < lineLengths.length
      ? lineLengths[currentLine - 1]
      : lineLengths[lineLengths.length - 1];
  if (nodes[end].type === "penalty") width += nodes[end].width;
  if (width < lineLength) {
    const stretch = sum.stretch - active.totals.stretch;
    if (stretch > 0) return (lineLength - width) / stretch;
    return INFINITY;
  }
  if (width > lineLength) {
    const shrink = sum.shrink - active.totals.shrink;
    if (shrink > 0) return (lineLength - width) / shrink;
    return INFINITY;
  }
  return 0;
};

const computeSum = (nodes: Node[], sum: Totals, breakPointIndex: number): Totals => {
  const result = { width: sum.width, stretch: sum.stretch, shrink: sum.shrink };
  for (let i = breakPointIndex; i < nodes.length; i += 1) {
    const node = nodes[i];
    if (node.type === "glue") {
      result.width += node.width;
      result.stretch += node.stretch;
      result.shrink += node.shrink;
    } else if (
      node.type === "box" ||
      (node.type === "penalty" && node.penalty === -INFINITY && i > breakPointIndex)
    ) {
      break;
    }
  }
  return result;
};

const findBestBreakpoints = (activeNodes: LinkedList): number[] => {
  const breakpoints: number[] = [];
  const candidates: Breakpoint[] = [];
  activeNodes.forEach((node) => candidates.push(node.data));
  if (candidates.length === 0) return [];
  // The best active node: the one with the least total demerits (the first
  // such node, as textkit's strict comparison keeps the earliest).
  let best = candidates[0];
  candidates.forEach((candidate) => {
    if (candidate.demerits < best.demerits) best = candidate;
  });
  let tmp: Breakpoint | null = best;
  while (tmp !== null) {
    breakpoints.push(tmp.position);
    tmp = tmp.previous;
  }
  return breakpoints.reverse();
};

const linebreak = (nodes: Node[], availableWidths: number[], tolerance: number): number[] => {
  const options = { demerits: { line: 10, flagged: 100, fitness: 3000 }, tolerance: tolerance || 3 };
  const activeNodes = new LinkedList();
  const sum: Totals = { width: 0, stretch: 0, shrink: 0 };
  const lineLengths = availableWidths;
  activeNodes.push(new ListNode(breakpoint(0, 0, 0, 0, undefined, null)));

  const mainLoop = (node: Node, index: number) => {
    let active = activeNodes.first();
    while (active !== null) {
      let currentLine = 0;
      const candidates: { active: ListNode | undefined; demerits: number }[] = [
        { active: undefined, demerits: Infinity },
        { active: undefined, demerits: Infinity },
        { active: undefined, demerits: Infinity },
        { active: undefined, demerits: Infinity },
      ];
      while (active !== null) {
        currentLine = active.data.line + 1;
        const ratio = computeCost(nodes, lineLengths, sum, index, active.data, currentLine);
        if (ratio < -1 || (node.type === "penalty" && node.penalty === -INFINITY)) {
          activeNodes.remove(active);
        }
        if (ratio >= -1 && ratio <= options.tolerance) {
          const badness = 100 * Math.pow(Math.abs(ratio), 3);
          let demerits = 0;
          if (node.type === "penalty" && node.penalty >= 0) {
            demerits = Math.pow(options.demerits.line + badness, 2) + Math.pow(node.penalty, 2);
          } else if (node.type === "penalty" && node.penalty !== -INFINITY) {
            demerits = Math.pow(options.demerits.line + badness, 2) - Math.pow(node.penalty, 2);
          } else {
            demerits = Math.pow(options.demerits.line + badness, 2);
          }
          const prev = nodes[active.data.position];
          if (node.type === "penalty" && prev.type === "penalty") {
            demerits += options.demerits.flagged * node.flagged * prev.flagged;
          }
          let currentClass: number;
          if (ratio < -0.5) currentClass = 0;
          else if (ratio <= 0.5) currentClass = 1;
          else if (ratio <= 1) currentClass = 2;
          else currentClass = 3;
          if (Math.abs(currentClass - active.data.fitnessClass) > 1) {
            demerits += options.demerits.fitness;
          }
          demerits += active.data.demerits;
          if (demerits < candidates[currentClass].demerits) {
            candidates[currentClass] = { active, demerits };
          }
        }
        active = active.next;
        if (active !== null && active.data.line >= currentLine) break;
      }
      const tmpSum = computeSum(nodes, sum, index);
      for (let fitnessClass = 0; fitnessClass < candidates.length; fitnessClass += 1) {
        const candidate = candidates[fitnessClass];
        if (candidate.demerits === Infinity || candidate.active === undefined) continue;
        const newNode = new ListNode(
          breakpoint(
            index,
            candidate.demerits,
            candidate.active.data.line + 1,
            fitnessClass,
            tmpSum,
            candidate.active.data,
          ),
        );
        if (active !== null) activeNodes.insertBefore(active, newNode);
        else activeNodes.push(newNode);
      }
    }
  };

  nodes.forEach((node, index) => {
    if (node.type === "box") {
      sum.width += node.width;
      return;
    }
    if (node.type === "glue") {
      const precedesBox = index > 0 && nodes[index - 1].type === "box";
      if (precedesBox) mainLoop(node, index);
      sum.width += node.width;
      sum.stretch += node.stretch;
      sum.shrink += node.shrink;
      return;
    }
    if (node.type === "penalty" && node.penalty !== INFINITY) {
      mainLoop(node, index);
    }
  });
  return findBestBreakpoints(activeNodes);
};

// Best-fit fallback: textkit uses it when Knuth-Plass finds no feasible set
// of breaks even at tolerance 50 (a word wider than the column).
const skipPastGlueAndPenalty = (nodes: Node[], start: number): Node => {
  let j = start + 1;
  for (; j < nodes.length; j += 1) {
    if (nodes[j].type !== "glue" && nodes[j].type !== "penalty") break;
  }
  return nodes[j - 1];
};

const getNextBreakpoint = (subnodes: Node[], widths: number[], lineNumber: number): number | null => {
  let position: number | null = null;
  let minimumBadness = Infinity;
  const sum: Totals = { width: 0, stretch: 0, shrink: 0 };
  const lineLength = widths[Math.min(lineNumber, widths.length - 1)];
  const calculateRatio = (node: Node) => {
    const stretch = node.type === "glue" ? node.stretch : null;
    if (sum.width < lineLength) {
      if (!stretch) return INFINITY;
      return sum.stretch - stretch > 0 ? (lineLength - sum.width) / sum.stretch : INFINITY;
    }
    const shrink = node.type === "glue" ? node.shrink : null;
    if (sum.width > lineLength) {
      if (!shrink) return INFINITY;
      return sum.shrink - shrink > 0 ? (lineLength - sum.width) / sum.shrink : INFINITY;
    }
    return 0;
  };
  let hyphenWidth = 0;
  for (let i = 0; i < subnodes.length; i += 1) {
    const node = subnodes[i];
    if (node.type === "box") sum.width += node.width;
    if (node.type === "glue") {
      sum.width += node.width;
      sum.stretch += node.stretch;
      sum.shrink += node.shrink;
    }
    const potentialEndOfLine = skipPastGlueAndPenalty(subnodes, i);
    hyphenWidth = potentialEndOfLine.type === "penalty" ? potentialEndOfLine.width : 0;
    if (sum.width - sum.shrink + hyphenWidth > lineLength) {
      if (position === null) {
        let j = i === 0 ? i + 1 : i;
        while (j < subnodes.length && (subnodes[j].type === "glue" || subnodes[j].type === "penalty")) {
          j += 1;
        }
        position = j - 1;
      }
      break;
    }
    if (node.type === "penalty" || node.type === "glue") {
      const ratio = calculateRatio(node);
      const penalty = node.type === "penalty" ? node.penalty : 0;
      const badness = 100 * Math.abs(ratio) ** 3 + penalty;
      if (minimumBadness >= badness) {
        position = i;
        minimumBadness = badness;
      }
    }
  }
  return sum.width - sum.shrink + hyphenWidth > lineLength ? position : null;
};

const applyBestFit = (nodes: Node[], widths: number[]): number[] => {
  let count = 0;
  let lineNumber = 0;
  let subnodes = nodes;
  const breakpoints = [0];
  while (subnodes.length > 0) {
    const bp = getNextBreakpoint(subnodes, widths, lineNumber);
    if (bp !== null) {
      count += bp;
      breakpoints.push(count);
      subnodes = subnodes.slice(bp + 1, subnodes.length);
      count += 1;
      lineNumber += 1;
    } else {
      subnodes = [];
    }
  }
  return breakpoints;
};

// The Knuth-Plass nodes textkit builds: words are tokenised per script run
// (wrapWords runs per run, pushing onto ONE syllable list), a box per word,
// glue per run of spaces, then the final glue and forced break. A word whose
// next syllable is not exactly one space gets a hyphen break opportunity
// after it, as in textkit.
const buildNodes = (
  chars: string[],
  adv: number[],
  runs: ShapingRun[],
  subLine = false,
  // Character indexes a soft hyphen stood before: a word is cut into
  // syllables there (textkit's wrapWords splits the word at the soft hyphen
  // and drops it), which gives it the hyphen break opportunity below.
  breakAfter: Set<number> = new Set(),
): Node[] => {
  const syllables: string[] = [];
  runs.forEach(({ start, end }) => {
    let i = start;
    while (i < end) {
      const isSpace = chars[i] === " ";
      let j = i;
      while (j < end && (chars[j] === " ") === isSpace) {
        j += 1;
        if (!isSpace && breakAfter.has(j)) break;
      }
      syllables.push(chars.slice(i, j).join(""));
      i = j;
    }
  });
  const nodes: Node[] = [];
  let start = 0;
  syllables.forEach((token, index) => {
    const length = Array.from(token).length;
    const width = adv.slice(start, start + length).reduce((s, a) => s + a, 0);
    if (token.trim() === "") {
      nodes.push({
        type: "glue",
        width,
        stretch: (width * GLUE_OPTS.width) / GLUE_OPTS.stretch,
        shrink: (width * GLUE_OPTS.width) / GLUE_OPTS.shrink,
      });
    } else {
      const hyphenated = syllables[index + 1] !== " ";
      nodes.push({ type: "box", width });
      if (syllables[index + 1] && hyphenated) {
        nodes.push({ type: "penalty", width: 5, penalty: 600, flagged: 1 });
      }
    }
    start += length;
  });
  // A cell with a sub line is one Text whose value is followed by the sub
  // run's newline. U+000A has no glyph in Inter, so react-pdf substitutes the
  // standard font and measures it 0pt; what the newline really adds is the
  // hyphen penalty textkit inserts after a word whose next syllable is not a
  // space (the newline is that next syllable) plus the substituted run's own
  // empty glue. A value that ends in a space ends in glue, not a word, so it
  // gets the glue alone.
  if (subLine) {
    const last = syllables[syllables.length - 1];
    if (last !== undefined && last.trim() !== "") {
      nodes.push({ type: "penalty", width: 5, penalty: 600, flagged: 1 });
    }
    nodes.push({ type: "glue", width: 0, stretch: 0, shrink: 0 });
  }
  nodes.push({ type: "glue", width: 0, stretch: INFINITY, shrink: 0 });
  nodes.push({ type: "penalty", width: 0, penalty: -INFINITY, flagged: 1 });
  return nodes;
};

// The node indexes react-pdf breaks a text at (position 0 first).
const lineBreaks = (
  chars: string[],
  adv: number[],
  runs: ShapingRun[],
  availableWidthPt: number,
  subLine: boolean,
  breakAfter: Set<number>,
): { nodes: Node[]; breaks: number[] } => {
  const nodes = buildNodes(chars, adv, runs, subLine, breakAfter);
  const widths = [availableWidthPt];
  let tolerance = 4;
  let breaks = linebreak(nodes, widths, tolerance);
  while (breaks.length === 0 && tolerance < TOLERANCE_LIMIT) {
    tolerance += TOLERANCE_STEPS;
    breaks = linebreak(nodes, widths, tolerance);
  }
  if (breaks.length === 0 || (breaks.length === 1 && breaks[0] === 0)) {
    breaks = applyBestFit(nodes, widths);
  }
  return { nodes, breaks };
};

// A measurer over the four embedded faces. Line counts are memoised (the
// planner asks for the same cells again whenever the picker re-plans).
const CACHE_LIMIT = 20000;

export const createTextMeasurer = (fonts: Record<Face, ShapingFont>): TextMeasurer => {
  const cache = new Map<string, number>();
  const faceOrThrow = (face: Face): ShapingFont => {
    const font = fonts[face];
    if (!font) throw new Error(`No embedded font for face "${face}"`);
    return font;
  };
  const lineHeight = (face: Face, size: number) => {
    const f = faceOrThrow(face);
    return ((f.ascent - f.descent + f.lineGap) / f.unitsPerEm) * size;
  };
  const lines = (
    raw: string,
    face: Face,
    size: number,
    letterSpacing: number,
    availableWidthPt: number,
    subLine = false,
  ) => {
    // A soft hyphen is a break opportunity inside its word (the callback in
    // fonts.ts splits there); the character itself never reaches the
    // breaker, as textkit drops it from every syllable before measuring.
    const { chars, breakAfter } = splitSoftHyphens(raw);
    const text = chars.join("");
    if (text.length === 0) return 1;
    const font = faceOrThrow(face);
    // A width that is not a real number means a caller built its column
    // widths wrong. One line per cell would look plausible and plan nonsense.
    if (!Number.isFinite(availableWidthPt)) {
      throw new Error(`Available width must be a number, got ${availableWidthPt}`);
    }
    const key = `${face}\u0001${size}\u0001${letterSpacing}\u0001${availableWidthPt}\u0001${subLine}\u0001${raw}`;
    const hit = cache.get(key);
    if (hit !== undefined) return hit;
    const runs = shapingRuns(chars, font);
    const adv = advances(chars, font, size, letterSpacing, runs);
    const { nodes, breaks } = lineBreaks(chars, adv, runs, availableWidthPt, subLine, breakAfter);
    // breakLines: every breakpoint after the first starts a new line, except
    // the mandatory final one; then the remainder is the last line.
    const inner = breaks.slice(1).filter((bp) => bp !== nodes.length - 1);
    const count = inner.length + 1;
    // One export of 1000 rows measures a few thousand distinct cells; past
    // the cap the oldest entry goes rather than being held for the life of
    // the tab, since the planner only ever re-asks for the current report.
    if (cache.size >= CACHE_LIMIT) {
      const oldest = cache.keys().next();
      if (!oldest.done) cache.delete(oldest.value);
    }
    cache.set(key, count);
    return count;
  };
  const width = (raw: string, face: Face, size: number, letterSpacing: number) => {
    const { chars } = splitSoftHyphens(raw);
    if (chars.length === 0) return 0;
    const key = `w\u0001${face}\u0001${size}\u0001${letterSpacing}\u0001${chars.join("")}`;
    const hit = cache.get(key);
    if (hit !== undefined) return hit;
    const font = faceOrThrow(face);
    const runs = shapingRuns(chars, font);
    const total = advances(chars, font, size, letterSpacing, runs).reduce((sum, a) => sum + a, 0);
    if (cache.size >= CACHE_LIMIT) {
      const oldest = cache.keys().next();
      if (!oldest.done) cache.delete(oldest.value);
    }
    cache.set(key, total);
    return total;
  };
  return { lines, lineHeight, width };
};

// The characters of a text without its soft hyphens, and the indexes (into
// that character list) a soft hyphen stood before.
const splitSoftHyphens = (raw: string): { chars: string[]; breakAfter: Set<number> } => {
  const chars: string[] = [];
  const breakAfter = new Set<number>();
  for (const ch of raw) {
    if (ch === SOFT_HYPHEN) breakAfter.add(chars.length);
    else chars.push(ch);
  }
  return { chars, breakAfter };
};
